// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import { isFirstCareUser } from "@modules/account/selectors";
import actionCreators from "@modules/service/actionCreator";
import type {
  DynamicQuestionsResponse,
  DynamicQuestionRevision,
} from "@modules/service/reducer/dynamic-questions/types";
import { ApprovalStateEnum } from "@modules/service/constants";
import type {
  HierarchyAssignment,
  ApprovableContainer,
  HierarchyAssignmentSummary,
} from "@modules/service/types";
import {
  GetDynamicQuestions,
  GetDynamicQuestionActive,
  AddNewDynamicQuestion,
  GetDynamicQuestion,
  UpdateDynamicQuestion,
  SubmitDynamicQuestionForReview,
  CancelDynamicQuestionReview,
  StartDynamicQuestionDraft,
  DiscardDynamicQuestionDraft,
  CancelDynamicQuestionReviewAndDiscard,
  DuplicateDynamicQuestion,
  DisableDynamicQuestion,
  UpdateDynamicQuestionAssignments,
} from "@modules/service/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";
type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ItemResponse<T> = $Exact<T>;

type DynamicQuestionsListRequest = $Exact<{
  clientId: string,
}>;

type AddDynamicQuestionsRequest = $Exact<{
  clientId: string,
  draft: DynamicQuestionRevision,
  assignments: Array<HierarchyAssignment>,
}>;

export type DynamicQuestionRequest = $Exact<{
  ...DynamicQuestionsListRequest,
  stableId: string,
}>;

export type DynamicQuestionSubmitForReviewRequest = $Exact<{
  ...DynamicQuestionRequest,
  notes: string,
}>;
type DynamicQuestionUpdateRequest = $Exact<{
  ...DynamicQuestionRequest,
  draft: DynamicQuestionRevision,
}>;

export function* onGetDynamicQuestions({
  payload,
}: Action<DynamicQuestionsListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<DynamicQuestionsResponse>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question`,
    );

    yield put(actionCreators.GetDynamicQuestionsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetDynamicQuestionsError(e.response));
  }
}

export function* onGetDynamicQuestionActive({
  payload,
}: Action<DynamicQuestionRequest>): Generator<*, void, { data: DynamicQuestionRevision }> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      // TODO check v2
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question/${payload.stableId}`,
    );

    yield put(actionCreators.GetDynamicQuestionActiveSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetDynamicQuestionActiveError(e.response));
  }
}

export function* onAddNewDynamicQuestion({
  payload,
}: Action<AddDynamicQuestionsRequest>): Generator<*, void, { data: DynamicQuestionRevision }> {
  try {
    if (!payload || !payload.clientId || !payload.draft || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      // TODO check v2
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question`,
      payload.draft,
    );

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            // TODO check v2
            `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question/${response.data.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield all([
      put(actionCreators.GetDynamicQuestionActiveSuccess(response.data)),
      put(actionCreators.GetDynamicQuestions({ clientId: payload.clientId })),
      put(actionCreators.ShowAddDynamicQuestionModal({ show: false, step: 1 })),
      put(sendSuccessMessage("DYNAMIC_QUESTION_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.GetDynamicQuestionActiveError(e.response));
  }
}

export function* onGetDynamicQuestion({
  payload,
}: Action<DynamicQuestionRequest>): Generator<*, void, { data: DynamicQuestionRevision }> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const stableResponse = yield call(
      http.get,
      // TODO check v2
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question/${payload.stableId}`,
    );
    const assignmentsResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question/${payload.stableId}/assignment`,
    );

    yield put(actionCreators.GetDynamicQuestionSuccess(stableResponse.data));
    yield put(actionCreators.SetDynamicQuestionAssignments(assignmentsResponse.data));
  } catch (e) {
    yield put(actionCreators.GetDynamicQuestionError(e.response));
  }
}

export function* onUpdateDynamicQuestion({
  payload,
}: Action<DynamicQuestionUpdateRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<DynamicQuestionRevision>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.stableId || !payload.draft) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question/${payload.stableId}`,
      payload.draft,
    );

    yield all([
      put(actionCreators.UpdateDynamicQuestionSuccess(response.data)),
      put(actionCreators.ShowDynamicQuestionEditModal({ show: false })),
      put(push(route("ADMINISTRATION_SERVICE_DYNAMIC_QUESTIONS", { clientId: payload.clientId }))),
    ]);
    yield put(sendSuccessMessage("DRAFT_UPDATED"));
  } catch (e) {
    yield put(actionCreators.UpdateDynamicQuestionError(e.response));
  }
}

export function* onSubmitDynamicQuestionForReview({
  payload,
}: Action<DynamicQuestionSubmitForReviewRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId || !payload.notes) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question/${payload.stableId}/submit-for-review`,
      {
        notes: payload.notes,
      },
    );

    yield all([
      put(actionCreators.SubmitDynamicQuestionForReviewSuccess()),
      put(actionCreators.SetShowSubmitDynamicQuestionModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_DYNAMIC_QUESTIONS", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.SubmitDynamicQuestionForReviewError(e.response));
  }
}

export function* onCancelDynamicQuestionReview({
  payload,
}: Action<DynamicQuestionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question/${payload.stableId}/cancel-review`,
    );

    yield all([
      put(actionCreators.CancelDynamicQuestionReviewSuccess()),
      put(actionCreators.SetShowCancelDynamicQuestionModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_DYNAMIC_QUESTIONS", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelDynamicQuestionReviewError(e.response));
  }
}

export function* onStartDynamicQuestionDraft({
  payload,
}: Action<DynamicQuestionRequest>): Generator<*, void, { data: DynamicQuestionRevision }> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question/${payload.stableId}`,
    );

    yield all([
      put(actionCreators.StartDynamicQuestionDraftSuccess(response.data)),
      put(actionCreators.ShowDynamicQuestionEditModal({ show: true, step: 1 })),
    ]);
  } catch (e) {
    yield put(actionCreators.StartDynamicQuestionDraftError(e.response));
  }
}

export function* onDiscardDynamicQuestionDraft({
  payload,
}: Action<DynamicQuestionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question/${payload.stableId}/draft`,
    );

    yield all([
      put(actionCreators.DiscardDynamicQuestionDraftSuccess()),
      put(actionCreators.SetShowDiscardDynamicQuestionModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_DYNAMIC_QUESTIONS", { clientId: payload.clientId }))),
    ]);
    yield call(onRefresh, { clientId: payload.clientId });
  } catch (e) {
    yield put(actionCreators.DiscardDynamicQuestionDraftError(e.response));
  }
}

export function* onCancelDynamicQuestionReviewAndDiscard({
  payload,
}: Action<DynamicQuestionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question/${payload.stableId}/cancel-review`,
    );
    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question/${payload.stableId}/draft`,
    );

    yield all([
      put(actionCreators.CancelDynamicQuestionReviewSuccess()),
      put(actionCreators.SetShowCancelDiscardDynamicQuestionModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_DYNAMIC_QUESTIONS", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelDynamicQuestionReviewAndDiscardError(e.response));
  }
}

export function* onDuplicateDynamicQuestion({
  payload,
}: Action<DynamicQuestionRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<DynamicQuestionRevision>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question/${payload.stableId}/active`,
    );

    yield all([
      put(actionCreators.DuplicateDynamicQuestionSuccess(response.data)),
      put(actionCreators.SetDynamicQuestionAssignments([])),
      put(actionCreators.ShowAddDynamicQuestionModal({ show: true, step: 1 })),
      put(push(route("ADMINISTRATION_SERVICE_DYNAMIC_QUESTIONS", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.DuplicateDynamicQuestionError(e.response));
  }
}

export type DynamicQuestionDisableRequest = $Exact<{
  ...DynamicQuestionRequest,
  approvable: ApprovableContainer<DynamicQuestionRevision>,
}>;

export function* onDisableDynamicQuestion({
  payload,
}: Action<DynamicQuestionDisableRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }
    const draft =
      payload.approvable.approvalState === ApprovalStateEnum.ACTIVE_ONLY
        ? payload.approvable.active
        : payload.approvable.draft;

    if (!draft) {
      throw Error("Invalid draft");
    }

    const response = yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question/${payload.stableId}/draft`,
      {
        ...draft,
        isActive: !draft.isActive,
      },
    );

    yield all([
      put(actionCreators.DisableDynamicQuestionSuccess(response.data)),
      put(actionCreators.SetShowDisableDynamicQuestionModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_DYNAMIC_QUESTIONS", { clientId: payload.clientId }))),
    ]);
    yield call(onRefresh, { clientId: payload.clientId });
    yield put(sendSuccessMessage("DRAFT_UPDATED"));
  } catch (e) {
    yield put(actionCreators.DisableDynamicQuestionError(e.response));
  }
}

// REFRESH

type RefreshRequest = $Exact<{
  clientId: string,
  stableId?: string,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  try {
    yield put(actionCreators.GetDynamicQuestions({ clientId: params.clientId }));
    if (params.stableId) {
      yield put(
        actionCreators.GetDynamicQuestion({ clientId: params.clientId, stableId: params.stableId }),
      );
    }
  } catch (e) {
    throw e;
  }
}

type DynamicQuestionUpdateAssignmentsRequest = $Exact<{
  container: ApprovableContainer<DynamicQuestionRevision>,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdateDynamicQuestionAssignments({
  payload,
}: Action<DynamicQuestionUpdateAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.container || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const { data: prevAssignments } = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/dynamic-question/question/${payload.container.id}/assignment`,
    );
    if (prevAssignments && prevAssignments.length) {
      yield all([
        ...prevAssignments.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/dynamic-question/question/${payload.container.id}/assignment/${a.id}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/dynamic-question/question/${payload.container.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield call(onRefresh, {
      clientId: payload.container.clientId,
      stableId: payload.container.id,
    });

    yield all([
      put(actionCreators.ShowEditDynamicQuestionAssignmentsModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DYNAMIC_QUESTIONS", {
            clientId: payload.container.clientId,
          }),
        ),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateDynamicQuestionAssignmentsError(e.message));
  }
}

export function* DynamicQuestionsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetDynamicQuestions, onGetDynamicQuestions),
    takeEvery(GetDynamicQuestionActive, onGetDynamicQuestionActive),
    takeEvery(AddNewDynamicQuestion, onAddNewDynamicQuestion),
    takeEvery(GetDynamicQuestion, onGetDynamicQuestion),
    takeEvery(UpdateDynamicQuestion, onUpdateDynamicQuestion),
    takeEvery(SubmitDynamicQuestionForReview, onSubmitDynamicQuestionForReview),
    takeEvery(CancelDynamicQuestionReview, onCancelDynamicQuestionReview),
    takeEvery(StartDynamicQuestionDraft, onStartDynamicQuestionDraft),
    takeEvery(DiscardDynamicQuestionDraft, onDiscardDynamicQuestionDraft),
    takeEvery(CancelDynamicQuestionReviewAndDiscard, onCancelDynamicQuestionReviewAndDiscard),
    takeEvery(DuplicateDynamicQuestion, onDuplicateDynamicQuestion),
    takeEvery(DisableDynamicQuestion, onDisableDynamicQuestion),
    takeEvery(UpdateDynamicQuestionAssignments, onUpdateDynamicQuestionAssignments),
  ]);
}

export default DynamicQuestionsSagas;
